// Frontend MDM calc engine — mirrors backend calc_engine.py exactly.
// Uses config from /api/calc/config (cached in localStorage for offline/PWA).
import api from "./api";

const CATEGORIES = ["balvatika", "primary", "upper_primary", "high_school"];

export function roundJs(x) {
  return Math.floor(x * 100 + 0.5) / 100;
}

function parseDate(d) {
  if (d instanceof Date) return d;
  const [y, m, day] = String(d).split("T")[0].split("-").map(Number);
  return new Date(y, m - 1, day);
}

function jsWeekday(d) {
  return parseDate(d).getDay(); // 0=Sun..6=Sat
}

function dGE(a, b) { return parseDate(a).getTime() >= parseDate(b).getTime(); }
function dLE(a, b) { return parseDate(a).getTime() <= parseDate(b).getTime(); }

let CONFIG = null;

export async function loadConfig(force = false) {
  if (CONFIG && !force) return CONFIG;
  try {
    const { data } = await api.get("/calc/config");
    CONFIG = data;
    if (data.redacted) localStorage.setItem("pm_calc_config", JSON.stringify(data));
    else localStorage.removeItem("pm_calc_config");
  } catch (e) {
    const cached = localStorage.getItem("pm_calc_config");
    if (cached && JSON.parse(cached).redacted) CONFIG = JSON.parse(cached);
    else throw e;
  }
  return CONFIG;
}

export function getConfig() {
  if (CONFIG) return CONFIG;
  const cached = localStorage.getItem("pm_calc_config");
  if (cached) { CONFIG = JSON.parse(cached); return CONFIG; }
  return null;
}

function getEffectivePeriod(d, periods) {
  for (const p of periods) {
    const ef = p.effective_from;
    const et = p.effective_to;
    if (dGE(d, ef) && (!et || dLE(d, et))) return p;
  }
  return null;
}

function isLaddooActive(d, weekday, laddooPolicy) {
  if (![2, 4, 6].includes(weekday)) return false;
  for (const pol of laddooPolicy) {
    if (dGE(d, pol.start) && (!pol.end || dLE(d, pol.end)) && pol.active && pol.days.includes(weekday)) return true;
  }
  return false;
}

function getVisibleItems(category, d, period, cfg) {
  const weekday = jsWeekday(d);
  const available = period.rates[category].items;
  const may2025 = "2025-05-01";
  if (dGE(d, may2025)) {
    const weekdayItems = cfg.weekday_items[weekday] || cfg.weekday_items[String(weekday)] || [];
    const visible = {};
    for (const name of weekdayItems) if (available[name]) visible[name] = { rate_per_student: 0, ...available[name] };
    return visible;
  }
  const visible = {};
  for (const k of Object.keys(available)) visible[k] = { rate_per_student: 0, ...available[k] };
  if (!isLaddooActive(d, weekday, cfg.laddoo_policy) && visible["Laddoo"]) delete visible["Laddoo"];
  return visible;
}

function autoBalance(category, visible, d, period) {
  const target = period.rates[category].target_total;
  if (!target || !dGE(d, "2025-05-01")) return visible;
  let sumNon = 0;
  for (const [name, it] of Object.entries(visible)) if (name !== "Vegetables") sumNon += it.rate_per_student;
  if (visible["Vegetables"]) {
    visible["Vegetables"] = { ...visible["Vegetables"], rate_per_student: roundJs(Math.max(0, target - sumNon)), is_auto_balanced: true };
  }
  return visible;
}

function categoryBreakdown(category, count, d, period, cfg) {
  if (count === 0) return { category, student_count: 0, items: {}, subtotal: 0, per_student_total: 0, rice_quantity_kg: 0 };
  let visible = getVisibleItems(category, d, period, cfg);
  visible = autoBalance(category, visible, d, period);
  const items = {}; let subtotal = 0; let riceG = 0;
  for (const [name, it] of Object.entries(visible)) {
    const totalQty = it.qty_per_student * count;
    const totalCost = it.rate_per_student * count;
    items[name] = { qty_per_student: it.qty_per_student, unit: it.unit, rate_per_student: it.rate_per_student,
      total_quantity: totalQty, total_cost: totalCost, emoji: it.emoji, is_auto_balanced: !!it.is_auto_balanced };
    subtotal += totalCost;
    if (name === "Rice") riceG += totalQty;
  }
  return { category, student_count: count, items, subtotal: roundJs(subtotal),
    per_student_total: count > 0 ? roundJs(subtotal / count) : 0, rice_quantity_kg: roundJs(riceG / 1000) };
}

export function calculateReport(d, students) {
  const cfg = getConfig();
  if (!cfg) throw new Error("Config not loaded");
  const period = getEffectivePeriod(d, cfg.periods);
  if (!period) throw new Error("No rate period found for this date.");
  const categories = {}; const aggregated = {};
  let grand = 0, totalStudents = 0, totalRice = 0;
  for (const c of CATEGORIES) {
    const count = parseInt(students[c] || 0, 10);
    const r = categoryBreakdown(c, count, d, period, cfg);
    categories[c] = r;
    grand += r.subtotal; totalStudents += r.student_count; totalRice += r.rice_quantity_kg;
    for (const [name, it] of Object.entries(r.items)) {
      if (!aggregated[name]) aggregated[name] = { total_quantity: 0, total_cost: 0, unit: it.unit, emoji: it.emoji, avg_rate: 0 };
      aggregated[name].total_quantity += it.total_quantity;
      aggregated[name].total_cost += it.total_cost;
    }
  }
  for (const it of Object.values(aggregated)) if (it.total_quantity > 0) it.avg_rate = roundJs(it.total_cost / it.total_quantity);
  const weekday = jsWeekday(d);
  return {
    date: parseDate(d).toISOString().split("T")[0],
    weekday_name: cfg.day_names[weekday],
    weekday_name_od: cfg.day_names_od[weekday],
    weekday_number: weekday,
    menu_display: (cfg.menu_plans[weekday] || cfg.menu_plans[String(weekday)] || {}).display || "",
    laddoo_active: isLaddooActive(d, weekday, cfg.laddoo_policy),
    categories, aggregated_items: aggregated,
    summary: { total_students: totalStudents, grand_total: roundJs(grand),
      avg_per_student: totalStudents > 0 ? roundJs(grand / totalStudents) : 0, total_rice_kg: roundJs(totalRice) },
    period_name: period.name,
  };
}

export { CATEGORIES };
